import { 
  collection, doc, getDoc, setDoc, addDoc, getDocs, 
  onSnapshot, deleteDoc, updateDoc, serverTimestamp, Timestamp,
  query, where, orderBy
} from 'firebase/firestore';
import { firestore, FIRESTORE_COLECOES, calcularExpiracaoTTL } from '../firebase';
import type { 
  UsuarioFirestore, 
  EnderecoSalvoFirestore, 
  CorridaFirestore, 
  PontoEnderecoCorrida, 
  ParadaCorrida,
  FormaPagamentoCorrida 
} from '../types/firestore';

const CLIENT_STORAGE_KEY = 'abh_client_uid';

/**
 * Obter ou criar um ID de cliente local no navegador
 */
export function getOrCreateClientId(): string {
  let uid = localStorage.getItem(CLIENT_STORAGE_KEY);
  if (!uid) {
    uid = 'cli_' + Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
    localStorage.setItem(CLIENT_STORAGE_KEY, uid);
  }
  return uid;
}

/**
 * Buscar Perfil do Usuário
 */
export async function buscarPerfilCliente(uid: string): Promise<UsuarioFirestore | null> {
  try {
    const userRef = doc(firestore, FIRESTORE_COLECOES.USUARIOS, uid);
    const snap = await getDoc(userRef);
    if (snap.exists()) {
      return snap.data() as UsuarioFirestore;
    }
    return null;
  } catch (error) {
    console.error('Erro ao buscar perfil do cliente:', error);
    return null;
  }
}

/**
 * Escutar Perfil do Usuário em Tempo Real
 */
export function escutarPerfilCliente(uid: string, callback: (usuario: UsuarioFirestore | null) => void) {
  const userRef = doc(firestore, FIRESTORE_COLECOES.USUARIOS, uid);
  return onSnapshot(userRef, (snap) => {
    if (snap.exists()) {
      callback(snap.data() as UsuarioFirestore);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Erro ao escutar perfil do cliente:', error);
  });
}

/**
 * Salvar/Atualizar Perfil do Usuário no Firestore
 */
export async function salvarPerfilCliente(uid: string, dados: Partial<UsuarioFirestore>): Promise<void> {
  try {
    const userRef = doc(firestore, FIRESTORE_COLECOES.USUARIOS, uid);
    const snap = await getDoc(userRef);
    if (!snap.exists()) {
      const novoUsuario: UsuarioFirestore = {
        uid,
        nome: dados.nome || 'Cliente',
        empresa: dados.empresa || '',
        whatsapp: dados.whatsapp || '',
        cnpj_cpf: dados.cnpj_cpf || '',
        total_gasto_mes: 0,
        total_gasto_acumulado: 0,
        criado_em: serverTimestamp()
      };
      await setDoc(userRef, novoUsuario);
    } else {
      await updateDoc(userRef, { ...dados });
    }
  } catch (error) {
    console.error('Erro ao salvar perfil do cliente:', error);
    throw error;
  }
}

/**
 * Listar Endereços Salvos do Cliente
 */
export async function listarEnderecosSalvos(uid: string): Promise<EnderecoSalvoFirestore[]> {
  try {
    const colRef = collection(firestore, FIRESTORE_COLECOES.USUARIOS, uid, FIRESTORE_COLECOES.ENDERECOS_SALVOS);
    const snap = await getDocs(colRef);
    return snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as EnderecoSalvoFirestore[];
  } catch (error) {
    console.error('Erro ao listar endereços salvos:', error);
    return [];
  }
}

/**
 * Salvar Endereço na Subcoleção do Cliente
 */
export async function salvarEnderecoCliente(uid: string, endereco: Omit<EnderecoSalvoFirestore, 'id'>): Promise<string> {
  try {
    const colRef = collection(firestore, FIRESTORE_COLECOES.USUARIOS, uid, FIRESTORE_COLECOES.ENDERECOS_SALVOS);
    const docRef = await addDoc(colRef, endereco);
    return docRef.id;
  } catch (error) {
    console.error('Erro ao salvar endereço do cliente:', error);
    throw error;
  }
}

/**
 * Excluir Endereço Salvo do Cliente
 */
export async function excluirEnderecoCliente(uid: string, enderecoId: string): Promise<void> {
  try {
    const docRef = doc(firestore, FIRESTORE_COLECOES.USUARIOS, uid, FIRESTORE_COLECOES.ENDERECOS_SALVOS, enderecoId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Erro ao excluir endereço do cliente:', error);
    throw error;
  }
}

export interface CriarCorridaParams {
  cliente_id: string;
  coleta: PontoEnderecoCorrida;
  paradas: ParadaCorrida[];
  entrega: PontoEnderecoCorrida;
  valor_bruto: number;
  desconto_taxa_aplicada: number;
  valor_final: number;
  valor_total: number;
  forma_pagamento: FormaPagamentoCorrida;
  valor_pago?: number;
  troco_necessario?: number;
  observacoes?: string | null;
}

/**
 * Criar Nova Corrida no Firestore com status "PENDENTE_OPERADOR" e TTL de 20 dias
 */
export async function criarCorrida(params: CriarCorridaParams): Promise<string> {
  try {
    const agora = new Date();
    const expiracaoTTL = calcularExpiracaoTTL(agora);

    const corridaData: any = {
      cliente_id: params.cliente_id,
      status: 'PENDENTE_OPERADOR',
      coleta: params.coleta,
      paradas: params.paradas,
      entrega: params.entrega,
      valor_bruto: params.valor_bruto,
      desconto_taxa_aplicada: params.desconto_taxa_aplicada,
      valor_final: params.valor_final,
      valor_total: params.valor_final,
      forma_pagamento: params.forma_pagamento,
      valor_pago: params.valor_pago || 0,
      troco_necessario: params.troco_necessario || 0,
      observacoes: params.observacoes || null,
      criado_em: serverTimestamp(),
      expiracao_ttl: Timestamp.fromDate(expiracaoTTL)
    };

    const colRef = collection(firestore, FIRESTORE_COLECOES.CORRIDAS);
    const docRef = await addDoc(colRef, corridaData);
    return docRef.id;
  } catch (error) {
    console.error('Erro ao criar corrida no Firestore:', error);
    throw error;
  }
}

/**
 * Listar Histórico de Corridas do Cliente
 */
export async function listarHistoricoCorridasCliente(clienteId: string): Promise<CorridaFirestore[]> {
  try {
    const colRef = collection(firestore, FIRESTORE_COLECOES.CORRIDAS);
    const q = query(
      colRef, 
      where('cliente_id', '==', clienteId),
      orderBy('criado_em', 'desc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as CorridaFirestore[];
  } catch (error) {
    console.error('Erro ao listar histórico de corridas:', error);
    return [];
  }
}

/**
 * Ouvir Atualizações de uma Corrida em Tempo Real
 */
export function escutarCorrida(corridaId: string, callback: (corrida: CorridaFirestore | null) => void) {
  const docRef = doc(firestore, FIRESTORE_COLECOES.CORRIDAS, corridaId);
  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      callback({ id: docSnap.id, ...docSnap.data() } as CorridaFirestore);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Erro ao escutar corrida:', error);
  });
}

/**
 * Cancelar Corrida pelo Cliente
 */
export async function cancelarCorridaCliente(corridaId: string): Promise<void> {
  try {
    const docRef = doc(firestore, FIRESTORE_COLECOES.CORRIDAS, corridaId);
    await updateDoc(docRef, {
      status: 'cancelada'
    });
  } catch (error) {
    console.error('Erro ao cancelar corrida:', error);
    throw error;
  }
}

/**
 * [ADMIN] Listar Todos os Clientes
 */
export async function listarTodosClientes(): Promise<UsuarioFirestore[]> {
  try {
    const colRef = collection(firestore, FIRESTORE_COLECOES.USUARIOS);
    const snap = await getDocs(colRef);
    return snap.docs.map(doc => ({
      ...doc.data()
    })) as UsuarioFirestore[];
  } catch (error) {
    console.error('Erro ao listar todos os clientes:', error);
    return [];
  }
}

/**
 * [ADMIN] Excluir Cliente (apaga documento principal do usuário)
 */
export async function excluirClienteAdmin(uid: string): Promise<void> {
  try {
    const docRef = doc(firestore, FIRESTORE_COLECOES.USUARIOS, uid);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Erro ao excluir cliente:', error);
    throw error;
  }
}

/**
 * [ADMIN] Obter Estatísticas de Lucro (Geral, Dinheiro, Pix, Cartão)
 */
export async function obterEstatisticasLucro() {
  try {
    const colRef = collection(firestore, FIRESTORE_COLECOES.CORRIDAS);
    // Filtrar apenas corridas concluídas
    const q = query(colRef, where('status', '==', 'concluida'));
    const snap = await getDocs(q);
    
    let totalGeral = 0;
    let totalDinheiro = 0;
    let totalPix = 0;
    let totalCartao = 0;

    snap.docs.forEach(doc => {
      const data = doc.data() as CorridaFirestore;
      const valor = data.valor_total || 0;
      
      totalGeral += valor;

      if (data.forma_pagamento === 'dinheiro') {
        totalDinheiro += valor;
      } else if (data.forma_pagamento === 'pix') {
        totalPix += valor;
      } else if (data.forma_pagamento === 'cartao' || data.forma_pagamento === 'cartao_link') {
        totalCartao += valor;
      }
    });

    return { totalGeral, totalDinheiro, totalPix, totalCartao };
  } catch (error) {
    console.error('Erro ao obter estatísticas de lucro:', error);
    return { totalGeral: 0, totalDinheiro: 0, totalPix: 0, totalCartao: 0 };
  }
}

/**
 * [ADMIN] Cadastrar Cliente Manualmente
 */
export async function cadastrarClienteManual(dados: Partial<UsuarioFirestore>): Promise<void> {
  try {
    const novoUid = 'cli_' + Math.random().toString(36).substring(2, 9) + Date.now().toString(36);
    const userRef = doc(firestore, FIRESTORE_COLECOES.USUARIOS, novoUid);
    const novoUsuario: UsuarioFirestore = {
      uid: novoUid,
      nome: dados.nome || 'Cliente',
      empresa: dados.empresa || '',
      whatsapp: dados.whatsapp || '',
      cnpj_cpf: dados.cnpj_cpf || '',
      total_gasto_mes: 0,
      total_gasto_acumulado: 0,
      criado_em: serverTimestamp()
    };
    await setDoc(userRef, novoUsuario);
  } catch (error) {
    console.error('Erro ao cadastrar cliente manual:', error);
    throw error;
  }
}

